import { useMemo, useState } from 'react';
import CustomChip from 'components/selects/CustomChip';
import { Code } from 'models/common/CommonCode';
import { useMechUtTypes } from 'hooks/useMechUtTypes';
import { CommonUtil } from 'utils/CommonUtil';
import { utUnitOf } from '../constants';
import { useUtComparisonStore } from '../store/useUtComparisonStore';
import { UtKey } from '../types';
import { fmt } from '../utils/excelUtils';
import SectionBox from './SectionBox';

/** 증감 0 판정 오차 — 사용량을 합산한 값이라 정확히 0이 되지 않는다 */
const EPS = 1e-9;

/** 증가·감소 **각각** 이 건수까지만 그린다 (|Δ| 상위) */
const TOP_N = 12;

/**
 * 다른 화면(AnalysisReportPanel 등)과 같은 공통 함수로 디코드한다.
 */
const dec = (v: string): string => CommonUtil.unescapeHtml(v || '');

interface Bar {
  key: string;
  /** 설비명 — 1라인별 모드에서 Machine명이 비면 Main으로 대체한다 */
  name: string;
  main: string;
  line: string;
  process: string;
  eqGroup: string;
  /** V1 → 마지막 버전 총사용량 증감 */
  diff: number;
}

/**
 * ⑧ 증가 / 감소 설비 도표 — V1→V2 증감량을 가운데 0선 기준 좌우로 뻗는 다이버징
 * (토네이도) 막대로 그린다.
 *
 *   - 마크업·CSS: 647~674줄 / 874~878줄  → 스타일은 styles.ts의 `.tor-*`
 *   - 로직: renderTornado / drawTornado (2175~2232줄)
 *
 * ⚠ **UT 항목 선택을 이 섹션 안에 따로 둔다.** 상단 ②의 전역 선택(`currentUT`)은 ④ 분석표와
 *   ⑤ 세부 Summary의 필터라서, 그걸 공유하면 도표를 보려고 항목을 바꾸는 순간 위 표들이
 *   함께 걸러진다. 또 여기 탭은 **변동이 있는 항목만** 보여주므로 전역 목록과 구성도 다르다.
 *
 * 값은 추가 조회 없이 ④와 같은 분석 결과(AnalysisRow.ut[key].diffU)를 그대로 쓴다.
 */
const TornadoSection = () => {
  const analysisResult = useUtComparisonStore((s) => s.analysisResult);
  const isPerLineMode = useUtComparisonStore((s) => s.isPerLineMode);
  const utTypes = useMechUtTypes();
  /** 이 도표에서 고른 UT 항목 — 비어 있으면 아래에서 첫 항목으로 본다 */
  const [pickedUT, setPickedUT] = useState<UtKey | ''>('');

  // 조기 반환보다 위에 있어야 훅 순서가 깨지지 않는다
  const rows = analysisResult ?? [];

  /** 변동이 하나라도 있는 UT 항목만 칩으로 만든다 (원본 renderTornado와 같은 규칙) */
  const changedUts = useMemo(
    () => utTypes.filter((ut) => rows.some((r) => Math.abs(r.ut[ut.key]?.diffU ?? 0) > EPS)),
    [utTypes, rows]
  );

  /**
   * 실제로 그릴 항목 — 고른 항목이 목록에서 사라졌으면(UT 항목 변경/재분석) 첫 항목으로 되돌린다.
   * state를 effect로 맞추지 않고 파생값으로 두면 한 박자 늦게 그려지는 일이 없다.
   *
   * ⚠ Array.prototype.find는 쓰지 않는다 — target이 es5라 TS2550이 난다(tsconfig는 건드리지 않는다).
   */
  const activeUT = (changedUts.filter((u) => u.key === pickedUT)[0] ?? changedUts[0])?.key;

  const chipOptions: Code[] = useMemo(
    () => changedUts.map((ut) => ({ cmnCd: ut.key, cmnCdNm: ut.label })),
    [changedUts]
  );

  const { shown, incCount, decCount, maxAbs } = useMemo(() => {
    if (!activeUT) return { shown: [] as Bar[], incCount: 0, decCount: 0, maxAbs: 1 };
    const all: Bar[] = rows
      .filter((r) => Math.abs(r.ut[activeUT]?.diffU ?? 0) > EPS)
      .map((r) => ({
        // 키는 원본값으로 만든다 — 디코드하면 서로 다른 원본이 같은 키가 될 수 있다
        // (1라인별 모드는 Main+Machine+라인까지 합쳐야 행이 구별된다)
        key: `${r.machineKey}||${r.main}||${r.line}`,
        // 화면에 그리는 값만 디코드한다
        name: dec(r.machine || (isPerLineMode ? r.main : '')),
        main: dec(r.main),
        line: dec(r.line),
        process: dec(r.process),
        eqGroup: dec(r.eqGroup),
        diff: r.ut[activeUT].diffU,
      }))
      .sort((a, b) => b.diff - a.diff);

    // ⚠ 이름을 dec로 두면 위 .map()이 쓰는 헬퍼 dec()를 같은 블록에서 가려(TDZ)
    //   "Cannot access 'dec' before initialization"이 난다.
    const incBars = all.filter((b) => b.diff > 0);
    const decBars = all.filter((b) => b.diff < 0);
    // 내림차순이므로 증가는 앞에서, 감소는 뒤에서(가장 많이 줄어든 쪽) 잘라낸다
    const list = [...incBars.slice(0, TOP_N), ...decBars.slice(-TOP_N)];
    return {
      shown: list,
      incCount: incBars.length,
      decCount: decBars.length,
      maxAbs: Math.max(...list.map((b) => Math.abs(b.diff)), 1),
    };
  }, [rows, activeUT, isPerLineMode]);

  if (!analysisResult) return null;

  const unit = activeUT ? utUnitOf(activeUT) : '';
  const hiddenCount = incCount + decCount - shown.length;

  return (
    <SectionBox
      no="⑧"
      title="증가 / 감소 설비 도표"
      count={incCount + decCount}
      note={
        <>
          증가 <strong className="danger">{incCount.toLocaleString()}</strong>대 / 감소{' '}
          <strong className="success">{decCount.toLocaleString()}</strong>대
        </>
      }
    >
      {changedUts.length === 0 ? (
        <div className="empty-state">
          <p>증가/감소한 설비가 없습니다.</p>
        </div>
      ) : (
        <>
          {/* 이 도표 전용 항목 선택 — ②의 전역 선택과 별개다 (위 주석 참고) */}
          <div className="chip-row">
            <CustomChip
              options={chipOptions}
              defaultValue={activeUT}
              singleSelect
              /* 선택된 칩을 다시 눌러 해제되면(빈 값) 첫 항목으로 되돌린다 */
              onChange={(value) => setPickedUT(((value as string) || '') as UtKey | '')}
            />
          </div>

          <div className="tor-wrap">
            <div className="tor-legend">
              <span className="lg">
                <span className="sw inc" />
                증가
              </span>
              <span className="lg">
                <span className="sw dec" />
                감소
              </span>
              <span className="lg">
                <span className="ln-legend">N</span> 설비명 옆 숫자 = 라인(Line) No.
              </span>
              {isPerLineMode && (
                <span className="lg">
                  <span className="mn-legend">Main</span> 파란 배지 = Main 구분(동일 Machine명
                  구별용)
                </span>
              )}
              <span style={{ color: 'var(--text-light)' }}>
                막대·오른쪽 값 = 증감량(Δ){unit ? ` · 단위 ${unit}` : ''}
                {hiddenCount > 0
                  ? ` · |Δ| 상위 ${shown.length}건만 표시 (${hiddenCount}건 생략)`
                  : ''}
              </span>
            </div>

            {shown.length === 0 ? (
              <div className="empty-state">
                <p>표시할 데이터가 없습니다.</p>
              </div>
            ) : (
              <div className="tor-chart">
                {shown.map((b) => {
                  const pos = b.diff > 0;
                  /* 트랙 절반(50%)이 최대치 — 한쪽 방향으로만 뻗는다 */
                  const width = (Math.abs(b.diff) / maxAbs) * 50;
                  const tooltip = [
                    b.process,
                    b.eqGroup,
                    b.name,
                    isPerLineMode && b.main ? `Main:${b.main}` : '',
                    b.line ? `${b.line}라인` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <div className="tor-row" key={b.key} title={tooltip}>
                      <div className="tor-name">
                        {b.eqGroup && (
                          <>
                            <span className="eq">{b.eqGroup}</span>
                            {' · '}
                          </>
                        )}
                        {b.name}
                        {isPerLineMode && b.main && b.main !== b.name && (
                          <span className="mn">{b.main}</span>
                        )}
                        {b.line && <span className="ln">{b.line}</span>}
                      </div>
                      <div className="tor-track">
                        <div
                          className={`tor-fill ${pos ? 'pos' : 'neg'}`}
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <div className={`tor-val ${pos ? 'pos' : 'neg'}`}>
                        {pos ? '▲ +' : '▼ '}
                        {fmt(Math.abs(b.diff))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </SectionBox>
  );
};

export default TornadoSection;
